import * as React from "react";
import PropTypes from "prop-types";
import {
  TreeGridComponent,
  ColumnsDirective,
  ColumnDirective,
  Selection,
  Inject,
  Sort,
  Filter,
  ColumnChooser,
  Toolbar,
  Edit,
  Resize,
  RowDD,
  Freeze,
  ExcelExport,
  ColumnMenu,
} from "@syncfusion/ej2-react-treegrid";
import { Workbook } from "@syncfusion/ej2-excel-export";
import { useRef, useState, useEffect } from "react";
import { IoMdFunnel } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { setFilterParams, setSortingData, setRecordsPerPage, activePageInfo, skipRecords } from "../../../middleware/actions/gridAction";
import NewPagination from "../../components/newPagination/newPaginationPersonalizedReport";
import "./index.css";

const timeTemplate = (seconds) => {
  if (!seconds && seconds !== 0) return <span>NA</span>;

  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);

  if (mins === 0) {
    return <span>{secs} sec</span>;
  } else if (secs === 0) {
    return <span>{mins} min</span>;
  } else {
    return (
      <span>
        {mins} min {secs} sec
      </span>
    );
  }
};

const OPERATORS = {
  equal: "eq",
  notequal: "neq",
  notexist: "nex",
  contains: "cn",
  startswith: "stw",
  endswith: "enw",
  greaterthan: "gt",
  greaterthanorequal: "gte",
  lessthan: "lt",
  lessthanorequal: "lte",
};

function TreeDataGrid(props) {
  const {
    columnFields,
    fetch,
    showGridToolBar = true,
    height,
    rowDataBound = () => {},
    showBottomBorder = false,
    showResetFilter = false,
    childMapping = "",
    getGridRef = undefined,
    dataBound = undefined,
    showExportButton = false,
    defaultSortingOptions = {},
    showLoader = () => {},
    hideLoader = () => {},
    exportFileName = "Client Activity Tracker",
    setShouldFetch,
    shouldFetch,
    radioSelectedValue,
    totalCount,
  } = props;

  const dispatch = useDispatch();
  const gridState = useSelector((state) => state.gridState);
  const limit = gridState.recordsPerPage;
  const sideNavActiveState = useSelector((state) => state.sideNavState.active);
  const gridRef = useRef(null);
  const isFirstRender = useRef(true);
  const isResetting = useRef(false);
  const [rowData, setRowData] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sort, setSort] = useState("");
  const [clearAll, setClearAll] = useState(false);
  const [totals, setTotals] = useState({
    totalNoOfViews: 0,
    totalSpentOnViews: 0,
    totalViewSpentOnViews: 0,
    totalViewSumOfTotalContent: 0,
    totalConsumptionInsight: 0,
    totalConsumptionProfile: 0,
    totalConsumptionWebinar: 0,
  });

  useEffect(() => {
    if (shouldFetch) {
      isResetting.current = true;
      const params = {};
      setFilters([]);
      setSort("");
      dispatch(setFilterParams([]));
      dispatch(setSortingData(""));
      dispatch(activePageInfo(1));
      dispatch(setRecordsPerPage(20));
      dispatch(skipRecords(0));

      fetch(null, params)
        .then(async (res) => {
          const dataWithSrNo = res?.data?.data?.map((item, index) => ({
            srNo: index + 1,
            ...item,
          }));
          setRowData(dataWithSrNo);
          setTotals(res?.data?.overallTotal);
          res?.data?.data.length === 0 ? showNoRecordFound(true) : showNoRecordFound(false);
          hideLoader();
          // Defer grid clear operations to next tick after React has re-rendered
          setTimeout(() => {
            try {
              if (gridRef.current) {
                gridRef.current.clearFiltering();
                gridRef.current.clearSorting();
                if (gridRef.current.clearRowSelection) gridRef.current.clearRowSelection();
              }
            } catch (_) {
              // Grid may not be ready yet, safe to ignore
            }
            isResetting.current = false;
          }, 0);
        })
        .catch(() => {
          hideLoader();
          showNoRecordFound(true);
          isResetting.current = false;
        });
      setShouldFetch(false);
    }
  }, [shouldFetch]);

  if (getGridRef) {
    getGridRef(gridRef);
  }

  function handleColumnChooser(args) {
    const button = args.currentTarget;
    const buttonRect = button.getBoundingClientRect();
    const middleX = buttonRect.left + buttonRect.width / 2;
    const offsetX = middleX - (sideNavActiveState ? 260 : 248);
    gridRef.current.columnChooserModule.openColumnChooser(offsetX, 0);
  }

  const handleResetFilters = () => {
    if (isResetting.current) return;
    isResetting.current = true;
    setFilters([]);
    dispatch(setFilterParams([]));
    setSort("");
    dispatch(setSortingData(""));
    dispatch(activePageInfo(1));
    dispatch(setRecordsPerPage(20));
    dispatch(skipRecords(0));
    setTimeout(() => {
      try {
        if (gridRef.current) {
          gridRef.current.clearFiltering();
          gridRef.current.clearSelection();
          gridRef.current.clearSorting();
        }
      } catch (_) {
        // Safe to ignore if grid is mid-render
      }
      isResetting.current = false;
    }, 0);
  };

  const handleExportData = async () => {
    try {
      showLoader();

      const params = {};
      if (filters.length) params.filters = filters;
      if (sort !== "") params.sort = sort;
      params.limit = totalCount > 0 ? totalCount : 10000;
      params.skip = 0;
      params._isExport = true;

      const response = await fetch(null, params);
      const allData = response?.data?.data || [];

      const formatTimeForExport = (seconds) => {
        if (!seconds && seconds !== 0) return "0 min";
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        if (mins === 0 && secs !== 0) return `${secs} sec`;
        if (secs === 0) return `${mins} min`;
        return `${mins} min ${secs} sec`;
      };

      const toExcelValue = (value, field, isDateField) => {
        if (value === undefined || value === null) return "";
        if (Array.isArray(value)) return value.join(", ");
        if (field === "timeSpent" || field === "totalTimeSpent") return formatTimeForExport(value);
        if (isDateField) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
          }
        }
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
      };

      const allColumns = [
        { field: "srNo", headerText: "S.No", type: null },
        ...columnFields.map((col) => ({
          field: col.field,
          headerText: col.headerText || col.field,
          type: col.type,
        })),
      ];

      const dateFieldSet = new Set(
        columnFields.filter((col) => col.type === "date").map((col) => col.field)
      );

      const headerRow = {
        index: 1,
        cells: allColumns.map((col, ci) => ({
          index: ci + 1,
          value: col.headerText,
          style: { bold: true, wrapText: true },
        })),
      };

      const dataRows = allData.map((row, rowIdx) => ({
        index: rowIdx + 2,
        cells: allColumns.map((col, ci) => ({
          index: ci + 1,
          value: col.field === "srNo" ? rowIdx + 1 : toExcelValue(row[col.field], col.field, dateFieldSet.has(col.field)),
        })),
      }));

      const book = new Workbook(
        { worksheets: [{ name: "Sheet1", rows: [headerRow, ...dataRows] }] },
        "xlsx"
      );

      book.save(`${exportFileName}.xlsx`);
      hideLoader();
    } catch (error) {
      console.error("Export error:", error);
      hideLoader();
    }
  };

  const handleDataBound = () => {
    dataBound && dataBound();
    setIconAlignment();
    clearLocalGrid();
  };

  const clearLocalGrid = () => {
    // Iterate over localStorage and insert the keys that meet the condition into arr
    const arr = [];
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i).substring(0, 8) == "gridgrid") {
        arr.push(localStorage.key(i));
      }
    }
    for (let i = 0; i < arr.length; i++) {
      localStorage.removeItem(arr[i]);
    }
  };

  const handleActionComplete = (args) => {
    if (args.requestType === "refresh" || args.requestType === "filtering") {
      let dataResults = gridRef?.current?.dataResults?.result;
      let movablescrollbar = gridRef?.current?.element?.querySelector(".e-movablescrollbar");
      let frozenContent = gridRef?.current?.element?.querySelector(".e-grid .e-frozencontent.e-frozen-left-content");
      let frozenborder = frozenContent?.querySelector(".e-table");
      if (dataResults?.length > 0) {
        if (frozenContent) frozenContent.style.width = "min-content";
        if (frozenborder) frozenborder.style.borderRight = "2px solid #6c757d";
        if (movablescrollbar) movablescrollbar.style.display = "block";
      }
      if (args.requestType === "filtering" && dataResults?.length == 0) {
        showNoRecordFound(true);
      }
    }
    setIconAlignment();
    // to reve the existing operator and add the required operator
    if (args.columnType === "string") {
      var dropdownInstance = args.filterModel.dlgDiv.getElementsByClassName("e-dropdownlist")[0];
      if (args.requestType === "filterafteropen" && dropdownInstance) {
        if (["subscriptionProductType", "subscriptionType"].includes(args.columnName)) {
          args.filterModel.dlgObj.element.children[0].firstElementChild.children[0].style.display = "none";
        }
        dropdownInstance.ej2_instances[0].dataSource = [
          {
            value: "startswith",
            text: "Starts With",
          },
          {
            value: "endswith",
            text: "Ends With",
          },
          {
            value: "contains",
            text: "Contains",
          },
          {
            value: "equal",
            text: "Equal",
          },
          {
            value: "notequal",
            text: "Not Equal",
          },
        ];
      }
    }
  };

  function setIconAlignment() {
    const gridinstances = document.querySelector(".e-grid");
    const instancesNew = gridinstances && gridinstances.ej2_instances[0];
    const headercells = instancesNew && instancesNew.element.querySelectorAll(".e-headercell");
    for (let i = 0; i < headercells?.length; i++) {
      if (headercells[i].querySelector(".e-headertext")) {
        var headertextwidth = headercells[i].querySelector(".e-headertext").getBoundingClientRect().width;
        var headercelldivwidth = headercells[i].querySelector(".e-headercelldiv").getBoundingClientRect().width;
        let rightSortFiltermargin = 0;
        let distance = 10;
        let rightFilterMargin = 0;
        let sortIconWidth = 16;
        let headercellPaddingRight = 10;
        let headercellPaddingLeft = 10;
        if (headercells[i].classList.contains("e-leftalign")) {
          rightSortFiltermargin = headercelldivwidth - headertextwidth - (distance * 2 + headercellPaddingRight + headercellPaddingLeft);
          rightFilterMargin = headercelldivwidth - headertextwidth - (sortIconWidth + distance * 2 + headercellPaddingLeft + headercellPaddingLeft);
        }
        if (headercells[i].classList.contains("e-centeralign")) {
          rightSortFiltermargin = (headercelldivwidth - headertextwidth) / 2 - (sortIconWidth + distance);
          rightFilterMargin = (headercelldivwidth - headertextwidth) / 2 - (15 + sortIconWidth + distance);
        }
        if (headercells[i].classList.contains("e-rightalign")) {
          rightSortFiltermargin = headercelldivwidth - headertextwidth - (sortIconWidth + distance);
          rightFilterMargin = 2;
        }
        // change the filtericon margin

        if (headercells[i].querySelector(".e-filtermenudiv.e-icons.e-icon-filter")) {
          // change the filter icon position
          if (headercells[i].classList.contains("e-rightalign")) {
            headercells[i].querySelector(".e-filtermenudiv.e-icons.e-icon-filter").style.margin = "-21px " + 16 + "px" + " -18px 0px";
          } else if (headercells[i].classList.contains("e-leftalign")) {
            headercells[i].querySelector(".e-filtermenudiv.e-icons.e-icon-filter").style.margin = "-21px " + rightFilterMargin + "px" + " -18px 0px";
          } else {
            headercells[i].querySelector(".e-filtermenudiv.e-icons.e-icon-filter").style.margin = "-21px " + rightFilterMargin + "px" + " -18px 0px";
          }
          headercells[i].querySelector(".e-filtermenudiv.e-icons.e-icon-filter").style.padding = "6px 0px 6px 0px";
          headercells[i].querySelector(".e-filtermenudiv.e-icons.e-icon-filter").style.display = "block";
        }

        // change the sorticon margin
        if (headercells[i].querySelector(".e-sortfilterdiv.e-icons")) {
          // change the Sort icon position
          headercells[i].querySelector(".e-sortfilterdiv.e-icons").style.margin = "-23px " + rightSortFiltermargin + "px" + " -15px 0px";
        }
        headercells[i].querySelector(".e-sortfilterdiv.e-icons").style.display = "block";
      }
    }
  }

  // Function to show hide No Record Found Text
  const showNoRecordFound = (showNoRecordText) => {
    setTimeout(() => {
      if (!gridRef?.current?.element) return;
      let emptyRow = gridRef.current.element.querySelector(".e-gridcontent .e-content .e-emptyrow");
      let frozenContent = gridRef.current.element.querySelector(".e-grid .e-frozencontent.e-frozen-left-content");
      let frozenborder = frozenContent?.querySelector(".e-table");
      if (!showNoRecordText || !emptyRow) return;
      let emptyRowTD = emptyRow.querySelector("td");
      emptyRow.style.height = "106px";
      emptyRow.style.display = "table-row";
      emptyRowTD.style.display = "";
      emptyRowTD.style.textAlign = "center";
      emptyRowTD.innerText = "No Records Found";
      if (frozenborder) frozenborder.style.borderRight = "none";
      if (frozenContent) frozenContent.style.width = "max-content";
    }, 100);
  };

  const fetchGridData = () => {
    if (gridRef?.current) {
      const frozencontent = gridRef.current.element?.querySelector(".e-gridcontent .e-content .e-emptyrow td");
      if (gridRef?.current?.dataResults?.result?.length === 0 && frozencontent) frozencontent.innerText = "";
    }
    const params = {};
    if (filters.length) params.filters = filters;
    if (sort !== "") params.sort = sort;
    if (limit) params.limit = limit;
    showLoader();

    fetch(null, params)
      .then(async (res) => {
        const dataWithSrNo = res?.data?.data?.map((item, index) => ({
          srNo: (gridState.skip || 0) + index + 1,
          ...item,
        }));
        setRowData(dataWithSrNo);
        setTotals(res?.data?.overallTotal);
        if (gridRef?.current?.clearRowSelection) gridRef.current.clearRowSelection();
        res?.data?.data.length === 0 ? showNoRecordFound(true) : showNoRecordFound(false);
        hideLoader();
      })
      .catch(() => {
        hideLoader();
        showNoRecordFound(true);
      });
  };

  const cleanUpFilters = () => {
    isResetting.current = true;
    setClearAll(true);
    setFilters([]);
    if (gridRef.current) {
      gridRef.current.clearFiltering();
      gridRef.current.clearSorting();
    }
    isResetting.current = false;
  };

  useEffect(() => {
    cleanUpFilters();
  }, []);

  const handleActionBegin = (e) => {
    if (isResetting.current) return;

    let newSort = "";
    const newFilters = [];

    if (e.requestType === "sorting") {
      dispatch(activePageInfo(1));
      dispatch(setRecordsPerPage(20));
      dispatch(skipRecords(0));

      if (e?.direction === "Ascending") {
        newSort = `${e.columnName}:asc`;
      } else if (e?.direction === "Descending") {
        newSort = `${e.columnName}:desc`;
      }

      setSort(newSort);
      dispatch(setSortingData(newSort));
      fetchGridData();
    }

    if (e.action === "filter") {
      dispatch(activePageInfo(1));
      dispatch(setRecordsPerPage(20));
      dispatch(skipRecords(0));

      newFilters.push([e.currentFilteringColumn, OPERATORS[e?.currentFilterObject?.operator], e?.currentFilterObject?.value]);

      setFilters(newFilters);
      dispatch(setFilterParams(newFilters));
      fetchGridData();
    }

    if (e.action === "clearFilter") {
      dispatch(activePageInfo(1));
      dispatch(setRecordsPerPage(20));
      dispatch(skipRecords(0));
      setFilters([]);
      dispatch(setFilterParams([]));
      fetchGridData();
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchGridData();
  }, [filters, sort, limit, clearAll]);

  return (
    <div className="gridPanel">
      {showGridToolBar && (
        <nav className="navbar navbar-dark bg-dark rounded-top ">
          <div className="form-inline my-2 my-lg-0  " style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <div>
              {showExportButton && (
                <button
                  className="btn btn-outline-white active mr-sm-2 btn-sm action-button "
                  id="dataScreeningExport"
                  onClick={handleExportData}
                >
                  <i className="fas fa-download pr-1"></i>Export
                </button>
              )}
            </div>
            <div>
              {showResetFilter && (
                <button className="btn btn-outline-white active mr-sm-2 btn-sm  btn-dark " id="clearFilter" onClick={() => handleResetFilters()}>
                  <i className=" pr-1">
                    <IoMdFunnel />
                  </i>
                  Reset Filters
                </button>
              )}
              <button className="btn btn-outline-white active  btn-sm btn btn-dark" id="openColumnChooser" onClick={handleColumnChooser}>
                <i className="fa fa-columns pr-1"></i>Column Chooser
              </button>
            </div>
          </div>
        </nav>
      )}
      {rowData && (
        <div className="treegrid-root">
          <TreeGridComponent
            ref={gridRef}
            dataSource={rowData}
            gridLines="Both"
            treeColumnIndex={0}
            childMapping={childMapping}
            allowSorting={true}
            sortSettings={defaultSortingOptions}
            allowFiltering={true}
            allowExcelExport={true}
            allowSelection={false}
            filterSettings={{ type: "Menu", hierarchyMode: "Both" }}
            showColumnChooser
            height={height || "100%"}
            rowDataBound={rowDataBound}
            enableCollapseAll={true}
            actionComplete={handleActionComplete}
            loadingIndicator={{ indicatorType: "Spinner" }}
            dataBound={handleDataBound}
            clipMode="EllipsisWithTooltip"
            actionBegin={handleActionBegin}
            style={{
              height: "100%",
              maxHeight: "100%",
              maxWidth: "100%",
              overflowY: "auto",
              overflowX: "auto",
              borderBottom: showBottomBorder ? "1px solid rgba(209,213,219,0.5)" : "none",
            }}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="srNo"
                headerText="S.No"
                width={100}
                textAlign="Center"
                allowSorting={false}
                allowFiltering={false}
                showInColumnChooser={false}
              />
              {columnFields.map((cN, i) => (
                <ColumnDirective
                  key={"cD" + i}
                  field={cN.field}
                  headerText={cN.headerText ? cN.headerText : cN.field}
                  type={cN.type}
                  format={cN.type === "date" ? "dd-MM-yyyy" : undefined}
                  filterTemplate={cN.filterTemplate}
                  textAlign={cN.textAlign}
                  headerTextAlign={cN.headerTextAlign}
                  width={cN.width}
                  filter={{ ...cN.filter }}
                  template={cN.template}
                  columns={cN.columns}
                  isFrozen={cN.isFrozen ?? false}
                  showInColumnChooser={cN?.showInColumnChooser || cN?.showInColumnChooser === undefined ? true : false}
                  allowSorting={cN.allowSorting ?? false}
                  allowFiltering={cN.allowFiltering ?? false}
                  headerTemplate={cN.headerTemplate}
                />
              ))}
            </ColumnsDirective>
            <Inject services={[Sort, Filter, Resize, Freeze, Edit, ColumnMenu, Selection, ColumnChooser, Toolbar, RowDD, ExcelExport]} />
          </TreeGridComponent>
          {radioSelectedValue === "personalized_view_analysis_user" && (
            <div className="border">
              <table className="table table-bordered mb-0" style={{ width: "100%", background: "#ebebeb78", tableLayout: "fixed" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "100px", padding: 0, fontWeight: "bold" }} className="py-2">
                      <span className="pl-1">Total</span>
                    </td>
                    <td style={{ width: "20%", padding: 0 }} className="py-2"></td>
                     <td style={{ width: "20%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "30%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "15%", padding: 0, fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="pl-1">{totals?.totalNoOfViews}</span>
                    </td>
                    <td style={{ width: "15.5%", padding: 0, color: "#f94f5e", fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="pl-1">{timeTemplate(totals?.totalSpentOnViews)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {radioSelectedValue === "personalized_view_analysis_view" && (
            <div className="border">
              <table className="table table-bordered mb-0" style={{ width: "100%", background: "#ebebeb78", tableLayout: "fixed" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "100px", padding: 0, fontWeight: "bold" }} className="py-2">
                      <span className="pl-1">Total</span>
                    </td>
                    <td style={{ width: "18%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "18%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "20%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "16%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "14%", padding: 0, fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="pl-1">{totals?.totalViewSumOfTotalContent}</span>
                    </td>
                    <td style={{ width: "14.5%", padding: 0, color: "#f94f5e", fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="pl-1">{timeTemplate(totals?.totalViewSpentOnViews)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {radioSelectedValue === "personalized_view_analysis_consumption" && (
            <div className="border">
              <table className="table table-bordered mb-0" style={{ width: "100%", background: "#ebebeb78", tableLayout: "fixed" }}>
                <tbody>
                  <tr>
                    <td style={{ width: "100px", padding: 0, fontWeight: "bold" }} className="py-2">
                      <span className="mr-[8px]">Total</span>
                    </td>
                    <td style={{ width: "12%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "12%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "10%", padding: 0 }} className="py-2"></td>
                    <td style={{ width: "13%", padding: 0, fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="mr-2">{totals?.totalConsumptionInsight}</span>
                    </td>
                    <td style={{ width: "14%", padding: 0, color: "#f94f5e", fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="mr-2">{totals?.totalConsumptionProfile}</span>
                    </td>
                    <td style={{ width: "13%", padding: 0, color: "#f94f5e", fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="mr-2">{totals?.totalConsumptionWebinar}</span>
                    </td>
                    <td style={{ width: "11%", padding: 0, color: "#f94f5e", fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="mr-2">{totals?.totalViewSumOfTotalContent}</span>
                    </td>
                    <td style={{ width: "15.5%", padding: 0, color: "#f94f5e", fontWeight: "bold", textAlign: "center" }} className="py-2">
                      <span className="mr-2">{timeTemplate(totals?.totalSpentOnViewsInSeconds)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <NewPagination
            rowData={rowData}
            allowPagination={true}
            showLoader={showLoader}
            hideLoader={hideLoader}
            totalCount={totalCount}
            setRowData={setRowData}
            fetch={fetch}
            defaultPageView={50}
          />
        </div>
      )}
    </div>
  );
}

TreeDataGrid.propTypes = {
  fetch: PropTypes.func.isRequired,
  showLoader: PropTypes.func,
  hideLoader: PropTypes.func,
  columnFields: PropTypes.arrayOf(PropTypes.object),
};

TreeDataGrid.defaultProps = {
  fetch: () => {},
  deleteOne: () => {},
  deleteMany: () => {},
  showLoader: () => {},
  hideLoader: () => {},
  columnNames: {},
  columnFields: [{}],
};

export default TreeDataGrid;
